import clsx from 'clsx';
import React, { forwardRef } from 'react';
import Select, { components, DropdownIndicatorProps, GroupBase, MultiValueRemoveProps } from 'react-select';
import { MenuPortalProps } from 'react-select/dist/declarations/src/components/Menu';

import { useRootElement } from '@/hooks/useRootElement';

import cn from './style.module.sass';
import { useTranslation } from 'react-i18next';

export interface ISelectElement {
	value: string | number;
	label: string;
	isDisabled?: boolean;
	operatorId?: number;
	name?: string;
	id?: number;
}

export interface ISelect {
	options: ISelectElement[];
	placeholder: string;
	isError: boolean;
	errorText: string;
	isDisabled: boolean;
	onChangeHandler: (item: ISelectElement) => void;
	menuZIndex: number;
	value: ISelectElement;
	label: string;
	name: string;
	isMulti: boolean;
	size: 'small' | 'default';
	className: string;
	portalClassName: string;
	isRequiredShow?: boolean;

	[name: string]: unknown;
}

const DropdownIndicator = ({ selectProps, ...rest }: DropdownIndicatorProps) => {
	const { t } = useTranslation();
	return components.DropdownIndicator && (
		<components.DropdownIndicator {...rest} selectProps={selectProps}>
			<div className={clsx(cn.arrow, selectProps.menuIsOpen && cn.arrowOpened)}>
				<div className={cn.arrowImage}>
					<img src="/img/core/svg/arrow.svg" alt={t('common.more')} />
				</div>
			</div>
		</components.DropdownIndicator>
	);
}

const MultiValueRemove = ({ selectProps, ...rest }: MultiValueRemoveProps) => {
	const { t } = useTranslation();
	return components.DropdownIndicator && (
		<components.MultiValueRemove {...rest} selectProps={selectProps}>
			<div className={cn.multiValueRemove}>
				<div className={cn.multiValueRemoveImage}>
					<img src="/img/core/svg/multi-delete.svg" alt={t('common.del')} />
				</div>
			</div>
		</components.MultiValueRemove>
	);
}

const MenuPortal: React.FC<MenuPortalProps<unknown, boolean, GroupBase<unknown>> & { portalClassName: string }> = ({
	portalClassName,
	...rest
}) => components.MenuPortal && <components.MenuPortal {...rest} className={portalClassName} />;

const getValueTitle = (value?: ISelectElement | ISelectElement[] | null) => {
	const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

	return selectedValues
		.map((item) => item.label)
		.filter(Boolean)
		.join(', ');
};

const CustomSelect = forwardRef<HTMLInputElement, Partial<ISelect>>(
	(
		{
			options,
			placeholder = '',
			isError,
			errorText,
			isDisabled = false,
			value,
			onChangeHandler,
			menuZIndex = 1200,
			label,
			name,
			size,
			isMulti,
			className,
			portalClassName,
			isRequiredShow,
			fontWeight,
			title,
			...rest
		},
		ref
	) => {
		const menuPortalTarget = useRootElement('body');
		
		const { t } = useTranslation();

		const selectId = name && `select--${name}`;
		const valueTitle = getValueTitle(value as ISelectElement | ISelectElement[] | null);

		return (
			<div
				className={clsx(
					cn.selectWrapper,
					isError && cn.selectWrapperError,
					size === 'small' && cn.selectWrapperSmall,
					className
				)}
				title={
					typeof title === 'string'
						? title
						: size === 'small'
							? valueTitle || (!value && placeholder ? placeholder : '')
							: ''
				}
			>
				{label && (
					<label
						htmlFor={selectId}
						className={clsx(cn.label, cn[fontWeight as string], isRequiredShow && cn.isRequiredCheck)}
					>
						{label}
					</label>
				)}
				<Select
					components={{
						DropdownIndicator,
						MultiValueRemove,
						// eslint-disable-next-line react/no-unstable-nested-components
						MenuPortal: (props) => <MenuPortal portalClassName={portalClassName} {...props} />,
					}}
					options={options || []}
					isDisabled={isDisabled}
					onChange={onChangeHandler}
					className={clsx(cn.select, isMulti && cn.selectMulti, 'select', isDisabled && cn.disabled)}
					placeholder={placeholder}
					classNamePrefix="select-filters"
					value={value}
					menuPortalTarget={menuPortalTarget as HTMLElement}
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					styles={{ menuPortal: (base) => ({ ...base, zIndex: menuZIndex }) }}
					name={selectId}
					isMulti={isMulti}
					noOptionsMessage={() => t('common.nf')}
					// Рабочий код. Библиотека требует странные типы.
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					ref={ref}
					{...rest}
				/>
				{isError && <span className={cn.error}>{errorText}</span>}
			</div>
		);
	}
);

export default CustomSelect;
